import type {FC, KeyboardEvent} from 'react'
import {useState} from 'react'
import {Box, IconButton, TextField, Tooltip} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import {useCustomTranslation} from '@hooks/useCustomTranslation.ts'

interface InlineRenameProps {
  /** Current title; pre-fills the field and is the baseline for the no-op check. */
  value: string
  /** Called with the trimmed, changed, non-empty title. */
  onSave: (next: string) => void
  /** Called on Escape, blur, the cancel button, or an empty/unchanged commit. */
  onCancel: () => void
  maxLength?: number
}

/**
 * A small inline title editor: a text field plus confirm/cancel buttons.
 * Enter commits, Escape/blur cancels. Empty or unchanged commits cancel instead
 * of saving. Kept presentational (no data layer) so it is easy to test and reuse.
 */
const InlineRename: FC<InlineRenameProps> = ({value, onSave, onCancel, maxLength = 300}) => {
  const [t] = useCustomTranslation('alpinist')
  const [draft, setDraft] = useState(value)

  const commit = (): void => {
    const next = draft.trim()
    if (!next || next === value.trim()) {
      onCancel()
      return
    }
    onSave(next)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onCancel()
    }
  }

  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', maxWidth: 360}}>
      <TextField
        autoFocus
        size="small"
        variant="standard"
        fullWidth
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={onCancel}
        inputProps={{maxLength, 'aria-label': t('conversations_rename')}}
      />
      <Tooltip title={t('conversations_rename_save')}>
        {/* preventDefault on mouse-down keeps focus in the field so onBlur (cancel)
            does not fire before the click is handled. */}
        <IconButton
          size="small"
          aria-label={t('conversations_rename_save')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={commit}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('conversations_rename_cancel')}>
        <IconButton
          size="small"
          aria-label={t('conversations_rename_cancel')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onCancel}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export default InlineRename
